import { HydraUIAPIError } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';

/** How the domain resolved: one joinable org, or an unbreakable tie. */
export type JoinMatch = 'single' | 'multiple';

export type JoinRequestAccounts = {
  /** The verified signup email the join token was minted for. */
  email: string;
  /** The email's domain (e.g. "liviasarban.dev"), named in the single-match copy. */
  domain: string;
  /**
   * "single" — one resolved org, offer to join it. "multiple" — the selection hit
   * an unbreakable tie, so we couldn't auto-connect. Absent when the domain has
   * no joinable org.
   */
  match?: JoinMatch;
  /**
   * The org to submit the request against, set only when match is "single". A
   * dead-end tie returns none on purpose: the signup can't act on any of them,
   * and the ids would only reveal how many orgs share their domain.
   */
  account_id?: string;
};

/** Domain part of an email — the fallback when the backend doesn't send `domain` yet. */
function emailDomain(email: string): string {
  return email.split('@')[1] ?? '';
}

/**
 * Narrow the wire `match` to the two outcomes the page renders.
 *
 * Everything that isn't a match we can act on collapses to `multiple` — the
 * dead-end that still offers a link onward — rather than to `undefined`, which
 * auto-redirects into signup. That asymmetry is deliberate: a redirect is a
 * full-page navigation the user can't undo, so it's reserved for a domain we're
 * confident has no joinable org.
 *
 * - `single` without an `account_id` can't be submitted, so it isn't a match.
 * - An unrecognized value means the backend said something this build doesn't
 *   understand; guessing "no orgs" would silently retire the whole flow.
 * - No `match` at all but a populated `accounts` array is a services deploy
 *   predating #23891: the domain *did* match, so no-match is the wrong read.
 *   Remove this branch once #23891 is out everywhere.
 */
function normalizeMatch(raw: { match?: string; account_id?: string; accounts?: unknown }): JoinMatch | undefined {
  if (raw.match === 'single') {
    return raw.account_id != null && raw.account_id !== '' ? 'single' : 'multiple';
  }
  if (raw.match != null) {
    return 'multiple';
  }
  if (Array.isArray(raw.accounts) && raw.accounts.length > 0) {
    return 'multiple';
  }
  return undefined;
}

/**
 * List the outcome of matching a verified signup to an existing org.
 *
 * Hits the unauthenticated token-scoped endpoint; the token comes from the
 * signup verify-code response. A 404 covers every invalid-token shape
 * (missing, expired, consumed — deliberately indistinguishable) and resolves
 * to null so the page can show the link-expired state rather than throwing.
 */
export async function fetchJoinRequestAccounts(token: string): Promise<JoinRequestAccounts | null> {
  try {
    const data = await hydraUiApi.joinRequests.getJoinRequestTokensAccounts({
      params: { path: { token } },
    });
    // `domain`/`match`/`account_id` land in the generated client once the services
    // OpenAPI pin is bumped (services PR #23891). Read them off a widened type
    // until then, falling back to deriving the domain from the email. `match` is
    // widened to string rather than JoinMatch because it arrives unvalidated —
    // normalizeMatch is what makes it one of the two we render.
    const raw =
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      data as typeof data & {
        domain?: string;
        match?: string;
        account_id?: string;
      };
    return {
      email: raw.email,
      domain: raw.domain ?? emailDomain(raw.email),
      match: normalizeMatch(raw),
      account_id: raw.account_id,
    };
  } catch (error) {
    if (error instanceof HydraUIAPIError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export type CreateJoinRequestInput = {
  accountId: string;
  firstName: string;
  lastName: string;
};

/**
 * Ask an org's admins to let this signup join (consumes the join token).
 * The response is uniform ("sent") whether or not a live request already
 * existed, so callers can't distinguish a pending request from a declined one.
 * A 404 (token expired/consumed) propagates as HydraUIAPIError for the page
 * to map to the link-expired state.
 */
export async function createJoinRequest(token: string, input: CreateJoinRequestInput): Promise<void> {
  await hydraUiApi.joinRequests.joinRequestTokensJoinRequests({
    params: { path: { token } },
    body: {
      account_id: input.accountId,
      first_name: input.firstName,
      last_name: input.lastName,
    },
  });
}

/**
 * Signup URL that resumes at the password step for an already-verified email,
 * skipping the code re-entry. Used by the join screen's "create a new account
 * instead" links: the requester proved email ownership with the code, so
 * Ember rebuilds the flow and lands exactly where confirming the code lands.
 */
export function signupResumeHref(email: string): string {
  return `/signup?email=${encodeURIComponent(email)}&resume=true`;
}
